import * as React from 'react';

import {Button} from 'shells/button/button';
import {Card} from 'shells/card/card';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Label} from 'shells/form/label';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {FooterBar} from 'shells/modal/footer-bar';
import {ModalLightbox} from 'shells/modal/modal-lightbox';
import {PathnameField} from 'shells/pathname-field/pathname-field';
import {TextField} from 'shells/textfield/textfield';
import {ModalHeader} from 'shells/typography/modal-header';

import {appHistory} from '../../../history';

import {useGetLandingPageDomain, useCreateLandingPage} from '../graphql/hooks';

import styles from './name-page-modal.module.css';

type Props = {
    onClickBack: () => void;
    name?: string | null | undefined;
    selectedStarterId: string;
};

export function NamePageModal(props: Props) {
    const {onClickBack, name, selectedStarterId} = props;

    const [pageName, setPageName] = React.useState<string>(name || '');
    const [urlPath, setUrlPath] = React.useState<string>('');

    const {landingPageDomain} = useGetLandingPageDomain();

    const {createLandingPage, isCreating, error, duplicateSlugError, unsafeUrlError} =
        useCreateLandingPage();

    const isSaveEnabled = Boolean(pageName && urlPath && !isCreating);
    const isErrorGeneric = Boolean(error && !duplicateSlugError && !unsafeUrlError);

    const handleSave = () => {
        if (isSaveEnabled) {
            createLandingPage(pageName, urlPath, selectedStarterId).then((newPagePath) =>
                appHistory.push(newPagePath)
            );
        }
    };

    const getErrorText = () => {
        if (duplicateSlugError) {
            return 'This path is already being used by another page; please choose something unique.';
        } else if (unsafeUrlError) {
            return 'Only allowed characters are letters, numbers, underscores, and hyphens; please choose something else.';
        }
    };

    return (
        <ModalLightbox onClose={onClickBack}>
            <Card hasBorder={true} hasShadow={false} backgroundColor='white' borderColor='white'>
                <div className={styles['modal-content']}>
                    {error && isErrorGeneric && (
                        <FlashBanner duration={2000} isOverlay={true} type='error'>
                            {error}
                        </FlashBanner>
                    )}
                    <ModalHeader>Name &amp; configure this page</ModalHeader>
                    <StackLegacy spacing={20}>
                        <div>
                            <Label htmlFor='page-name'>
                                <strong>Name</strong>
                            </Label>
                            <TextField
                                id='page-name'
                                placeholder={name || 'My new page'}
                                value={pageName}
                                onChange={setPageName}
                                onEnter={handleSave}
                                selectOnFocus={true}
                                focusOnMount={true}
                            />
                            <div className={styles['helper-text']}>
                                This is just for you and your own organization
                            </div>
                        </div>
                        <div>
                            <Label htmlFor='page-url-path'>
                                <strong>URL path</strong>
                            </Label>
                            <PathnameField
                                domainName={
                                    landingPageDomain
                                        ? `https://${landingPageDomain}/`
                                        : `https://${NutshellFrontendSettings.urlShortenerRoot}/`
                                }
                                placeholder='my-new-page'
                                value={urlPath}
                                onChange={setUrlPath}
                                onEnter={handleSave}
                                hasError={Boolean(error && !isErrorGeneric)}
                                initialDomainWidth={landingPageDomain ? undefined : 109}
                            />
                            {error && !isErrorGeneric ? (
                                <span className={styles['error-text']}>{getErrorText()}</span>
                            ) : undefined}
                        </div>
                    </StackLegacy>
                    <FooterBar
                        left={
                            <Button onClick={onClickBack} variant='secondary' size='big'>
                                Back
                            </Button>
                        }
                        right={
                            <Button
                                onClick={handleSave}
                                variant='save'
                                size='big'
                                disabled={!isSaveEnabled}
                            >
                                {isCreating ? 'Creating…' : 'Save'}
                            </Button>
                        }
                    />
                </div>
            </Card>
        </ModalLightbox>
    );
}
